import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight } from 'lucide-react';
import FavoriteButton from './FavoriteButton';

/**
 * ListCard — the shared result-card shell for the entity list pages.
 *
 * Encapsulates the card <Link>, the responsive row layout, the title/badge
 * row, and the right-hand meta cluster (optional rulebook name, favorite
 * heart, chevron) so mobile-layout fixes live in ONE place instead of ten.
 * The per-entity content rides in as slots:
 *
 *   title        string — the entity name (rendered as the h3)
 *   badges       node   — rendered inline after the title in the wrapping
 *                         title row (badges, inline meta, icons)
 *   children     node   — body rows below the title row (meta lines, grids);
 *                         direct children are spaced by the column's space-y-2
 *   actions      node   — extra per-card controls rendered in the right-hand
 *                         cluster beside the favorite heart (e.g. the compare
 *                         toggle, issue 188); each control must preventDefault/
 *                         stopPropagation on click, as FavoriteButton does,
 *                         because it sits inside the card's <Link>
 *   rulebookName string — opts into the Spells-style right cluster: BookOpen
 *                         icon + name on the right (hidden below `sm`, where
 *                         the full name squeezes the title at phone widths)
 *                         and the card stacking vertically below `sm`.
 *                         Pass a string (may be empty) to opt in; omit for
 *                         the plain favorite+chevron cluster.
 *
 * NAVIGATION CONTRACT (see CLAUDE.md): `to`, `state` ({ from: url }) and
 * `onClick` (analytics track + useListNav saveState) are passed straight
 * through to the <Link>. This component must never alter or wrap them.
 */
const ListCard = ({
  to,
  state,
  onClick,
  title,
  ariaLabel,
  badges = null,
  actions = null,
  rulebookName,
  showFavorite = false,
  entityType,
  slug,
  alignCenter = false,
  className = 'dnd-card hover:shadow-md transition-shadow duration-200 group',
  children = null,
}) => {
  // The rulebook-name cluster is wide, so those cards stack vertically on
  // phones; the plain favorite+chevron cluster is narrow and stays inline.
  const stacked = rulebookName !== undefined && rulebookName !== null;
  const rowClass = stacked
    ? 'flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2'
    : `flex ${alignCenter ? 'items-center' : 'items-start'} justify-between`;

  return (
    <Link
      to={to}
      state={state}
      onClick={onClick}
      className={className}
      tabIndex={0}
      aria-label={ariaLabel || `View details for ${title}`}
      data-preview={entityType && slug ? `${entityType}/${slug}` : undefined}
    >
      <div className={rowClass}>
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {/* h2, not h3 (#355): a list page's only heading above this is
                the page's own h1, so an h3 card title skipped a level on every
                list in the app. The level is semantics — size comes from
                `spell-title` — so the cards look exactly as they did. */}
            <h2 className="spell-title text-base">{title}</h2>
            {badges}
          </div>
          {children}
          {/* The source book on PHONES, as its own line in the card body.
              The right-hand cluster below is hidden under `sm` on purpose — the
              full title squeezes the headline at phone widths — but hiding the
              cluster used to hide the BOOK with it, and four shipped lists
              (Races, Monsters, Skills, Items) printed it in the body exactly
              like this, at every width. Unifying every Family onto the cluster
              therefore dropped the book from 454 phone cards. Rendering it here
              restores it without competing for the headline's row, because it is
              no longer on that row. */}
          {stacked && rulebookName && (
            <div className="spell-rulebook sm:hidden">
              <BookOpen className="spell-rulebook-icon" aria-hidden="true" />
              <span>{rulebookName}</span>
            </div>
          )}
        </div>

        <div className={stacked ? 'spell-rulebook flex-shrink-0' : 'flex items-center flex-shrink-0'}>
          {stacked && (
            <>
              <BookOpen className="spell-rulebook-icon hidden sm:block" aria-hidden="true" />
              <span className="hidden sm:inline">{rulebookName}</span>
            </>
          )}
          {showFavorite && <FavoriteButton entityType={entityType} slug={slug} />}
          {actions}
          <ChevronRight className={stacked ? 'spell-arrow' : 'feature-arrow'} aria-hidden="true" />
        </div>
      </div>
    </Link>
  );
};

export default ListCard;
